import { useMemo, useState } from 'react';
import {
  ActionableNotification,
  Button,
  Dropdown,
  DropdownSkeleton,
  Modal,
  SkeletonText,
} from '@carbon/react';
import classNames from 'classnames';
import { useAtomValue } from 'jotai';
import routes from '~/routes';
import { format0 } from 'utils/number';
import { LaunchLink } from 'components/launch-link';
import { useTypeCurveScenarioOptions, useTypeCurveSummary } from './hooks';
import { typeCurveOverridesAtom, inventoryPackageOverridesAtom } from './atoms';
import type {
  InventoryPackageOverride,
  TypeCurveOverride,
  TypeCurveScenarioOption,
  TypeCurveSummaryResponse,
} from './types';
import {
  CreateInventoryWorkspaceModal,
  type EvalResolvedRow,
} from 'components/type-curve-workspace/create-inventory-workspace-modal';
import { useInventoryFormationCandidates } from 'components/type-curve-workspace/use-inventory-formation-candidates';
import { EvalModalLtdLayer } from './eval-modal-ltd-layer';
import styles from './type-curve-scenario-section.module.scss';

/**
 * Display text for a scenario Dropdown item — tolerates the `null` Carbon's `itemToString`
 * contract can pass.
 *
 * @param {TypeCurveScenarioOption | null} scenario - The scenario option, or `null`.
 * @return {string} The scenario's name, or an empty string for `null`.
 */
const scenarioToString = (scenario: TypeCurveScenarioOption | null): string =>
  scenario?.name ?? '';

/**
 * The ledger rows under the dropdown — one descriptor each, so the label / hover title /
 * loading-skeleton / value treatment is written once and mapped.
 *
 * `warn` marks a figure that is a problem rather than a statistic: uncovered permits/DUCs have no
 * type curve, so they price at nothing.
 */
const LEDGER_ROWS: {
  label: string;
  title: string;
  skeletonWidth: string;
  value: (summary: TypeCurveSummaryResponse) => string;
  warn?: (summary: TypeCurveSummaryResponse) => boolean;
}[] = [
  {
    label: 'LTD Properties',
    title:
      'Undrilled locations (LTDs) this scenario brings into the package, after places already taken by a well are removed',
    skeletonWidth: '1.5rem',
    value: (summary) => format0(summary.ltdSlotCount),
  },
  {
    label: 'LTD Capable Units',
    title: "Units hosting at least one of this scenario's LTD places",
    skeletonWidth: '1.5rem',
    value: (summary) => format0(summary.ltdCapableUnitCount),
  },
  {
    label: 'Permits/DUCs',
    title:
      "Permits and DUCs in units this scenario doesn't cover have no type curve, so they won't be valued",
    skeletonWidth: '6rem',
    value: ({ permitsDucsCoverage: coverage }) => {
      if (coverage.uncovered > 0) {
        return `${format0(coverage.uncovered)} of ${format0(
          coverage.total
        )} uncovered`;
      }
      return `All ${format0(coverage.total)} covered`;
    },
    warn: ({ permitsDucsCoverage: coverage }) => coverage.uncovered > 0,
  },
];

export interface TypeCurveScenarioSectionProps {
  /** The page's package — scopes the ledger figures. */
  packageId: number;
  /**
   * The EFFECTIVE scenario id, or `null` — the BE then applies its own default (Legacy), which
   * the helper text explains rather than the FE guessing at it.
   */
  typeCurveScenarioId: number | null;
  /**
   * True when a scenario change would discard pending slot overrides — gates the confirm before
   * committing, since changing scenario swaps the whole LTD collection those overrides key on.
   */
  hasPendingOverrides: boolean;
  /** Commit a scenario change (the callsite clears the pending overrides). */
  onScenarioChange: (id: number) => void;
  /**
   * Commit the Manage Type Curves modal's Apply — curve and inventory-package deltas
   * (tombstones included), appended to the override log exactly as the retired bespoke
   * modal committed.
   */
  onTypeCurveOverridesChange: (
    deltas: TypeCurveOverride[],
    packageDeltas: InventoryPackageOverride[]
  ) => void;
  /**
   * A case's run: the scenario is the case's, named by its Type Curve row, so the section shows no
   * scenario field at all. The ledger and Manage Type Curves stay, on the case's scenario.
   */
  readOnly?: boolean;
  /**
   * The page's eval-calc id, or null (draft). Sent as-is, as master_data sends it: the modal
   * counts the run's verdict only for a curve and package the run priced, live otherwise.
   */
  calculationId?: number | null;
}

/**
 * The Type Curve Scenario block in Case management — a case-parent input like the price deck
 * beneath it, and the only way LTD places enter a package (INS-10669).
 *
 * Renders the scenario dropdown (helper text explains the Legacy default while nothing is
 * picked), then a ledger of what the selection brings in: LTD places, the units hosting them, and
 * how many permits/DUCs the scenario fails to cover. Every figure comes from the same resolver,
 * slot read and reconciler the slots table runs, so the panel cannot quote a place count the grid
 * disagrees with.
 *
 * Changing the scenario swaps the package's whole LTD collection, and slot overrides key on slot
 * ids that would no longer exist — so with pending overrides the pick raises a danger confirm
 * first; confirming commits through `onScenarioChange`, whose callsite clears them. With nothing
 * to lose the pick commits directly.
 *
 * "Manage Type Curves" opens the TC team's own modal in eval mode: package-anchored,
 * seeded with the committed override choices, and its Apply hands back every seen row's resolved
 * picks, which this section folds into curve / inventory-package deltas (tombstones for rows put
 * back on their default) and commits through `onTypeCurveOverridesChange` — the same log the
 * bespoke modal used, so every read surface and the evaluation pick them up unchanged.
 *
 * @param {TypeCurveScenarioSectionProps} props - The package identity, effective scenario, and commit callback.
 * @return {JSX.Element} The Type Curve Scenario block.
 */
export function TypeCurveScenarioSection({
  packageId,
  typeCurveScenarioId,
  hasPendingOverrides,
  onScenarioChange,
  onTypeCurveOverridesChange,
  readOnly = false,
  calculationId = null,
}: TypeCurveScenarioSectionProps) {
  // A pick awaiting the wipe confirm; `null` = no confirm open.
  const [pendingScenarioId, setPendingScenarioId] = useState<number | null>(
    null
  );
  const [managerOpen, setManagerOpen] = useState(false);

  // The EFFECTIVE manager choices (committed + pending, folded) — read from the published
  // atoms rather than threaded as props, matching useTypeCurveSummary, so the seeds and the
  // Apply baseline can never differ from what the read surfaces resolve with.
  const typeCurveOverrides = useAtomValue(typeCurveOverridesAtom);
  const inventoryPackageOverrides = useAtomValue(inventoryPackageOverridesAtom);

  // The modal seeds its dropdowns from these, keyed by its row id ("<area>:<formation>"),
  // so a managed zone opens on the eval's current choice rather than the server default.
  const evalInitialTypeCurves = useMemo(() => {
    const seeds: Record<string, number> = {};
    typeCurveOverrides.forEach((override) => {
      if (override.typeCurveHeaderDetailsId === null) return;
      seeds[`${override.typeCurveAreaName}:${override.formation}`] =
        override.typeCurveHeaderDetailsId;
    });
    return seeds;
  }, [typeCurveOverrides]);
  const evalInitialPackages = useMemo(() => {
    const seeds: Record<
      string,
      { name: string; wellSpacingFt: number | null }
    > = {};
    inventoryPackageOverrides.forEach((override) => {
      if (override.inventoryPackageName === null) return;
      seeds[`${override.typeCurveAreaName}:${override.formation}`] = {
        name: override.inventoryPackageName,
        wellSpacingFt: override.wellSpacingFt,
      };
    });
    return seeds;
  }, [inventoryPackageOverrides]);

  // The Apply baseline — the same folds, keyed as the override log keys its pairs.
  const committedTcByKey = useMemo(
    () =>
      new Map(
        typeCurveOverrides.map((override) => [
          `${override.typeCurveAreaId}:${override.formation}`,
          override.typeCurveHeaderDetailsId,
        ])
      ),
    [typeCurveOverrides]
  );
  const committedPkgByKey = useMemo(
    () =>
      new Map(
        inventoryPackageOverrides.map((override) => [
          `${override.typeCurveAreaId}:${override.formation}`,
          override,
        ])
      ),
    [inventoryPackageOverrides]
  );

  /**
   * Folds the modal's Apply into override deltas and commits them.
   *
   * Per changed pick, against the effective fold: a non-default pick that differs becomes an
   * override entry; a pick put back on its default (or that can no longer resolve a choice)
   * tombstones any existing entry. The modal returns only rows the operator changed, and
   * each field only counts when it was changed, so untouched choices are never cleared.
   *
   * @param {EvalResolvedRow[]} rows - The changed rows' resolved choices, from the modal.
   * @return {void}
   */
  const handleApplyEval = (rows: EvalResolvedRow[]) => {
    const tcDeltas: TypeCurveOverride[] = [];
    const pkgDeltas: InventoryPackageOverride[] = [];
    rows.forEach((row) => {
      if (row.typeCurveAreaId === null) return;
      const key = `${row.typeCurveAreaId}:${row.formation}`;
      const base = {
        typeCurveAreaId: row.typeCurveAreaId,
        typeCurveAreaName: row.typeCurveAreaName,
        formation: row.formation,
      };
      const committedTc = committedTcByKey.get(key);
      if (!row.typeCurveChanged) {
        // Untouched curve: keep whatever is committed.
      } else if (
        row.chosenTypeCurveHeaderDetailsId === null ||
        row.isDefaultTypeCurve
      ) {
        if (committedTc !== undefined) {
          tcDeltas.push({ ...base, typeCurveHeaderDetailsId: null });
        }
      } else if (row.chosenTypeCurveHeaderDetailsId !== committedTc) {
        tcDeltas.push({
          ...base,
          typeCurveHeaderDetailsId: row.chosenTypeCurveHeaderDetailsId,
        });
      }
      const committedPkg = committedPkgByKey.get(key);
      if (!row.packageChanged) {
        // Untouched package: keep whatever is committed.
      } else if (row.chosenPackageName === null || row.isDefaultPackage) {
        if (committedPkg) {
          pkgDeltas.push({
            ...base,
            inventoryPackageName: null,
            wellSpacingFt: null,
          });
        }
      } else if (
        !committedPkg ||
        committedPkg.inventoryPackageName !== row.chosenPackageName ||
        committedPkg.wellSpacingFt !== row.chosenPackageSpacingFt
      ) {
        pkgDeltas.push({
          ...base,
          inventoryPackageName: row.chosenPackageName,
          wellSpacingFt: row.chosenPackageSpacingFt,
        });
      }
    });
    if (tcDeltas.length > 0 || pkgDeltas.length > 0) {
      onTypeCurveOverridesChange(tcDeltas, pkgDeltas);
    }
    setManagerOpen(false);
  };

  const {
    data: scenarios,
    isPending: scenariosLoading,
    isError: scenariosError,
    refetch: refetchScenarios,
  } = useTypeCurveScenarioOptions();
  const selectedScenario =
    scenarios?.find((scenario) => scenario.id === typeCurveScenarioId) ?? null;

  const {
    data: baseSummary,
    isPending: summaryLoading,
    isError: summaryError,
    refetch: refetchSummary,
  } = useTypeCurveSummary({ packageId, typeCurveScenarioId });

  // The LTD figures come from the TC side's OWN counting (the package-anchored candidates
  // read, the same numbers the Manage Type Curves modal shows), so the ledger and the modal
  // cannot disagree.
  // Geometry is skipped — this reads three numbers, not a map. Permits/DUCs coverage stays on
  // the eval summary: permits are an eval concept the TC side has no counting for. With no
  // explicit scenario (the BE previews Legacy) the candidates read cannot run, and the eval
  // summary's own figures stand in.
  // The hook stays idle while there's no anchor, i.e. no scenario.
  const { summary: candidatesSummary } = useInventoryFormationCandidates(null, {
    enabled: true,
    packageAnchor:
      typeCurveScenarioId !== null
        ? { packageId, typeCurveScenarioId, includeGeometry: false }
        : null,
  });
  // With manager choices in play the candidates read no longer describes the eval — it is
  // defaults-only by design — so the ledger falls back to the eval summary, which folds the
  // override log (useTypeCurveSummary re-keys on it) and equals the candidates figures by
  // construction whenever the log is empty.
  const overridesInPlay =
    typeCurveOverrides.length > 0 || inventoryPackageOverrides.length > 0;
  const summary: TypeCurveSummaryResponse | undefined = baseSummary && {
    ...baseSummary,
    ltdSlotCount:
      !overridesInPlay && candidatesSummary
        ? candidatesSummary.ltdProperties
        : baseSummary.ltdSlotCount,
    ltdCapableUnitCount:
      !overridesInPlay && candidatesSummary
        ? (candidatesSummary.ltdUnitCount ?? baseSummary.ltdCapableUnitCount)
        : baseSummary.ltdCapableUnitCount,
  };

  // The link opens the SELECTED scenario's page; with nothing explicitly picked it falls back to
  // the Type Curves listing rather than guessing which scenario the BE defaulted to.
  const scenarioPageUrl =
    typeCurveScenarioId !== null
      ? `${routes.typeCurveScenariosPath}/${String(typeCurveScenarioId)}`
      : routes.typeCurvesPath;

  /**
   * Handles a dropdown pick: the same id is a no-op; pending overrides raise the wipe confirm;
   * otherwise the pick commits directly.
   *
   * @param {TypeCurveScenarioOption | null | undefined} scenario - The picked option.
   * @return {void}
   */
  const handlePick = (scenario: TypeCurveScenarioOption | null | undefined) => {
    if (!scenario || scenario.id === typeCurveScenarioId) {
      return;
    }
    if (hasPendingOverrides) {
      setPendingScenarioId(scenario.id);
      return;
    }
    onScenarioChange(scenario.id);
  };

  return (
    <div className={styles.section}>
      {/* While the list downloads, skeleton the field — a disabled dropdown reads as "empty",
          not "loading" — matching the ledger below, which skeletons its values. */}
      {readOnly ? null : scenariosLoading ? (
        <DropdownSkeleton size="sm" />
      ) : (
        <div className={styles.scenarioField}>
          <Dropdown<TypeCurveScenarioOption>
            id="eval3-type-curve-scenario"
            titleText="Type Curve Scenario"
            label="Select a type curve scenario"
            items={scenarios ?? []}
            itemToString={scenarioToString}
            // `null` (not undefined) keeps the Dropdown CONTROLLED for its whole life —
            // `?? undefined` would flip it uncontrolled→controlled when the options resolve (the
            // downshift warning). Carbon's prop type omits `null` even though downshift accepts it
            // as controlled-empty, hence the widening cast.
            selectedItem={
              selectedScenario as TypeCurveScenarioOption | undefined
            }
            onChange={({ selectedItem }) => handlePick(selectedItem)}
            size="sm"
            helperText={
              typeCurveScenarioId === null
                ? 'Previews the Legacy scenario until one is selected.'
                : undefined
            }
          />
          {/* Opens the selected scenario's Type Curve Scenarios page in a new tab — moved off the
              actions row and positioned INSIDE the picker field (CSS, just left of the chevron).
              It stays a real anchor and a DOM sibling of the field (not nested in the field's
              button), so it's valid + keyboard-reachable; LaunchLink bundles target/rel, and it's
              icon-only so it carries an aria-label. */}
          <LaunchLink
            className={styles.scenarioLaunch}
            to={scenarioPageUrl}
            label="Open Type Curve Scenarios in a new tab"
          />
        </div>
      )}
      {/* Read-only shows no scenario field, so the list it fills has nothing to fail. */}
      {scenariosError && !readOnly && (
        <ActionableNotification
          className={styles.notification}
          inline
          kind="error"
          // hasFocus off: its auto-focus scrolls the errored section into view (page jump).
          hasFocus={false}
          lowContrast
          hideCloseButton
          title="Couldn't load type curve scenarios"
          subtitle="The scenario list is unavailable right now."
          actionButtonLabel="Retry"
          onActionButtonClick={() => refetchScenarios()}
        />
      )}

      {/* What the selected scenario brings into the package. */}
      {summaryError ? (
        <ActionableNotification
          className={styles.notification}
          inline
          kind="error"
          hasFocus={false}
          lowContrast
          hideCloseButton
          title="Couldn't load type curve counts"
          subtitle="The scenario's figures are unavailable right now."
          actionButtonLabel="Retry"
          onActionButtonClick={() => refetchSummary()}
        />
      ) : (
        <dl className={styles.counts}>
          {LEDGER_ROWS.map(({ label, title, skeletonWidth, value, warn }) => (
            <div key={label} className={styles.countRow}>
              <dt className={styles.countLabel} title={title}>
                {label}
              </dt>
              <dd
                className={classNames(styles.countValue, {
                  [styles.countValueWarning]: summary ? warn?.(summary) : false,
                })}
              >
                {summaryLoading || !summary ? (
                  <SkeletonText
                    className={styles.countSkeleton}
                    width={skeletonWidth}
                  />
                ) : (
                  value(summary)
                )}
              </dd>
            </div>
          ))}
        </dl>
      )}

      <div className={styles.actions}>
        {/* Gated on a scenario being picked: with nothing chosen the BE previews Legacy, and a
            curve override made against that implicit default would be wiped the moment the user
            picked a scenario (setTypeCurveScenario clears them) — wasted work. A disabled Button
            swallows hover, so the reason rides on a non-disabled wrapper. */}
        {typeCurveScenarioId === null ? (
          <span
            className={styles.manageAction}
            title="Select a type curve scenario to manage its curves"
          >
            <Button kind="tertiary" size="sm" disabled>
              Manage Type Curves
            </Button>
          </span>
        ) : (
          <Button
            kind="tertiary"
            size="sm"
            onClick={() => setManagerOpen(true)}
          >
            Manage Type Curves
          </Button>
        )}
      </div>

      {/* The TC team's own modal, package-anchored: their table, columns, defaults,
          ordering and map, scoped server-side to this package's assets. Dropdowns open on the
          eval's committed choices; Apply hands the picks back and handleApplyEval commits them
          to the override log (phase 3) — Close still discards. */}
      {managerOpen && (
        <CreateInventoryWorkspaceModal
          open
          mode="eval"
          typeCurveHeaderDetailsId={null}
          packageId={packageId}
          calculationId={calculationId}
          typeCurveScenarioId={typeCurveScenarioId}
          evalInitialTypeCurves={evalInitialTypeCurves}
          evalInitialPackages={evalInitialPackages}
          onApplyEval={handleApplyEval}
          onClose={() => setManagerOpen(false)}
          // The LTD places over their map: the modal hands back the slot rows the table is
          // pointing at, and this draws them. Selecting a row also frames them (the modal's own
          // fitBounds), so the count in the row and the sticks under it are one answer.
          renderMapOverlay={(slotIds) => (
            <EvalModalLtdLayer
              packageId={packageId}
              scenarioId={typeCurveScenarioId}
              slotIds={slotIds}
            />
          )}
        />
      )}

      {/* Wipe confirm — changing the scenario swaps the LTD collection, so slot overrides keyed
          on the old place ids would no longer match anything. Conditionally MOUNTED (not just
          closed): a closed Carbon modal keeps its DOM, leaking its heading/buttons into the
          a11y tree. */}
      {pendingScenarioId !== null && (
        <Modal
          open
          danger
          size="sm"
          modalHeading="Change type curve scenario?"
          primaryButtonText="Change scenario"
          secondaryButtonText="Cancel"
          onRequestSubmit={() => {
            onScenarioChange(pendingScenarioId);
            setPendingScenarioId(null);
          }}
          onRequestClose={() => setPendingScenarioId(null)}
        >
          <p>
            Changing the type curve scenario will change the collection of LTDs
            in this package and will reset any slot-table overrides you&rsquo;ve
            made.
          </p>
        </Modal>
      )}
    </div>
  );
}
